'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import DashboardSubHeader from './components/DashboardSubHeader';
import AlertBanner from './components/AlertBanner';
import KPIRow from './components/KPIRow';
import HeadcountStatsRow from './components/HeadcountStatsRow';
import AlertCardsRow from './components/AlertCardsRow';
import ChartsRow from './components/ChartsRow';
import CenterHealthColumn from './components/CenterHealthColumn';
import ActivityFeedColumn from './components/ActivityFeedColumn';
import RealtimeDashboard from './components/RealtimeDashboard';
import { createClient } from '@/lib/supabase/client';

export interface DashboardData {
  companyId: string | null;
  companyName: string;
  employees: any[];
  payrollRecords: any[];
  auditLog: any[];
  onboarding: any[];
  candidates: any[];
  scheduledShifts: any[];
}

const EMPTY_DATA: DashboardData = {
  companyId: null,
  companyName: '',
  employees: [],
  payrollRecords: [],
  auditLog: [],
  onboarding: [],
  candidates: [],
  scheduledShifts: [],
};

export default function PlatformDashboardPage() {
  const [data, setData] = useState<DashboardData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const companyIdRef = useRef<string | null>(null);
  const channelRef = useRef<any>(null);

  const fetchDashboardData = useCallback(async (companyId: string, companyName: string) => {
    const supabase = createClient();
    const [empRes, payrollRes, auditRes, onboardingRes, candidatesRes, shiftsRes] = await Promise.all([
      supabase.from('hr_employees').select('id,first_name,last_name,department,employee_status,annual_salary,right_to_work,created_at').eq('company_id', companyId),
      supabase.from('payroll_records').select('id,monthly_gross,annual_salary,payroll_status,created_at').eq('company_id', companyId),
      supabase.from('hr_audit_log').select('id,action_type,entity_type,entity_label,performed_by_name,created_at').eq('company_id', companyId).order('created_at', { ascending: false }).limit(20),
      supabase.from('employee_onboarding').select('id,onboarding_status,candidate_name').eq('company_id', companyId),
      supabase.from('candidates').select('id,status,first_name,last_name').eq('company_id', companyId),
      supabase.from('scheduled_shifts')
        .select('id,shift_date,start_time,end_time,employee_id,parent_shift_id,positions_meta,status,location')
        .eq('company_id', companyId)
        .gte('shift_date', (() => { const d = new Date(); d.setDate(d.getDate() - 14); return d.toISOString().slice(0, 10); })())
        .lte('shift_date', (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })()),
    ]);

    setData({
      companyId,
      companyName,
      employees: empRes.data || [],
      payrollRecords: payrollRes.data || [],
      auditLog: auditRes.data || [],
      onboarding: onboardingRes.data || [],
      candidates: candidatesRes.data || [],
      scheduledShifts: shiftsRes.data || [],
    });
  }, []);

  // Keep a stable ref to fetchDashboardData so the useEffect below
  // never needs it as a dependency (avoids re-subscribing on every render).
  const fetchRef = useRef(fetchDashboardData);
  useEffect(() => { fetchRef.current = fetchDashboardData; }, [fetchDashboardData]);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    // Remove any stale channel before starting (handles React StrictMode double-invoke)
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    async function init() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) { setLoading(false); return; }

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();

        const companyId = profile?.company_id;
        if (!companyId || cancelled) { setLoading(false); return; }

        companyIdRef.current = companyId;

        const { data: company } = await supabase
          .from('company_defaults')
          .select('company_name')
          .eq('company_id', companyId)
          .single();

        const companyName = company?.company_name || '';

        await fetchRef.current(companyId, companyName);
        if (cancelled) return;

        // Build the full channel chain BEFORE calling subscribe()
        const channel = supabase
          .channel(`dashboard-kpi-${companyId}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'hr_employees', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'payroll_records', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'hr_audit_log', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'employee_onboarding', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'candidates', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'scheduled_shifts', filter: `company_id=eq.${companyId}` },
            () => { fetchRef.current(companyId, companyName); }
          )
          .subscribe();

        if (cancelled) {
          supabase.removeChannel(channel);
          return;
        }

        channelRef.current = channel;
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-12 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Loading dashboard…</span>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-12 py-6 space-y-5">
        <DashboardSubHeader companyName={data.companyName} />
        <AlertBanner />
        <KPIRow data={data} />
        <HeadcountStatsRow employees={data.employees} />
        <AlertCardsRow data={data} />
        <ChartsRow payrollRecords={data.payrollRecords} scheduledShifts={data.scheduledShifts} />
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          <div className="xl:col-span-1 space-y-5">
            <CenterHealthColumn data={data} />
            <RealtimeDashboard companyId={data.companyId} />
          </div>
          <div className="xl:col-span-2 2xl:col-span-3">
            <ActivityFeedColumn auditLog={data.auditLog} />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}